/**
 * Must outlast the 0.3s fade declared by `.theme-switching` in tokens.css. A
 * little longer is harmless — the class only matters while colours are moving.
 */
const THEME_TRANSITION_MS = 350;

export default function themeToggle() {
    return {
        mode: 'system',

        get isLight() {
            return this.mode === 'light';
        },

        get isDark() {
            return this.mode === 'dark';
        },

        get isSystem() {
            return this.mode === 'system';
        },

        get ariaLabel() {
            const next = { light: 'dark', dark: 'system', system: 'light' };
            return `Colour mode: ${this.mode}. Switch to ${next[this.mode]} mode`;
        },

        toggle() {
            this.mode = { light: 'dark', dark: 'system', system: 'light' }[this.mode];
            try {
                localStorage.setItem('theme', this.mode);
            } catch {
                // Keep the control usable when browser storage is unavailable.
            }
            this.applyTheme();
        },

        applyTheme() {

            // Arm the blanket colour transition in tokens.css for the length of
            // the fade, then disarm it. It is gated on this class because it is
            // universal and unlayered: left on permanently it REPLACES the
            // `transition` shorthand of every component rule in @layer
            // components, silently dropping any property those rules animate
            // that is not background-color / color / border-color. See the
            // comment beside `.theme-switching` in tokens.css.
            const root = document.documentElement;
            root.classList.add('theme-switching');
            window.clearTimeout(this._themeTimer);
            this._themeTimer = window.setTimeout(() => {
                root.classList.remove('theme-switching');
            }, THEME_TRANSITION_MS);

            root.classList.toggle('dark', this.isDark || (this.isSystem && this._media.matches));
        },

        init() {
            this._media = window.matchMedia('(prefers-color-scheme: dark)');
            try {
                const saved = localStorage.getItem('theme');
                this.mode = ['light', 'dark', 'system'].includes(saved) ? saved : 'system';
            } catch {
                this.mode = 'system';
            }
            this._onSystemChange = () => {
                if (this.isSystem) {
                    this.applyTheme();
                }
            };
            this._media.addEventListener('change', this._onSystemChange);
            this.applyTheme();
        },

        destroy() {
            this._media.removeEventListener('change', this._onSystemChange);
            window.clearTimeout(this._themeTimer);
            document.documentElement.classList.remove('theme-switching');
        },
    };
}
